import { T } from 'gt-react';
import type { ConnectionType } from '@erp/shared-types/workos';
import { Button } from 'src/components/ui/button';
import { Spinner } from 'src/components/ui/spinner';

interface SsoLoginButtonProps {
  connectionType: string | null | undefined;
  onClick: () => void;
  disabled: boolean;
  isLoading: boolean;
}

// Official Google sign-in branding asset (google.svg via gstatic.com/firebasejs)
// https://developers.google.com/identity/branding-guidelines
const GoogleLogo = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 120 120"
    width="18"
    height="18"
    aria-hidden="true"
  >
    {/* eslint-disable-next-line i18next/no-literal-string -- SVG branding asset title, not user-visible text */}
    <title>Google G</title>
    <path
      fill="#4285F4"
      d="M117.6,61.36C117.6,57.11 117.22,53.02 116.51,49.09L60,49.09L60,72.3L92.29,72.3C90.9,79.8 86.67,86.15 80.32,90.41L80.32,105.46L99.71,105.46C111.05,95.02 117.6,79.64 117.6,61.36Z"
    />
    <path
      fill="#34A853"
      d="M60,120C76.2,120 89.78,114.63 99.71,105.46L80.32,90.41C74.95,94.01 68.07,96.14 60,96.14C44.37,96.14 31.15,85.58 26.43,71.4L6.38,71.4L6.38,86.95C16.25,106.55 36.55,120 60,120Z"
    />
    <path
      fill="#FBBC05"
      d="M26.43,71.4C25.23,67.8 24.55,63.95 24.55,60C24.55,56.05 25.23,52.2 26.43,48.6L26.43,33.05L6.38,33.05C2.32,41.15 0,50.32 0,60C0,69.68 2.32,78.85 6.38,86.95L26.43,71.4Z"
    />
    <path
      fill="#EA4335"
      d="M60,23.86C68.81,23.86 76.72,26.89 82.94,32.84L100.15,15.63C89.75,5.95 76.17,0 60,0C36.55,0 16.25,13.45 6.38,33.05L26.43,48.6C31.15,34.42 44.37,23.86 60,23.86Z"
    />
  </svg>
);

// Official Microsoft identity platform branding asset (ms-symbollockup_mssymbol_19.svg)
// https://learn.microsoft.com/en-us/entra/identity-platform/howto-add-branding-in-apps
const MicrosoftLogo = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="21"
    height="21"
    viewBox="0 0 21 21"
    aria-hidden="true"
  >
    {/* eslint-disable-next-line i18next/no-literal-string -- SVG branding asset title, not user-visible text */}
    <title>MS-SymbolLockup</title>
    <rect x="1" y="1" width="9" height="9" fill="#f25022" />
    <rect x="1" y="11" width="9" height="9" fill="#00a4ef" />
    <rect x="11" y="1" width="9" height="9" fill="#7fba00" />
    <rect x="11" y="11" width="9" height="9" fill="#ffb900" />
  </svg>
);

const GenericSsoIcon = () => (
  <svg
    className="w-4 h-4 ml-2"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
    strokeWidth={2}
    aria-hidden="true"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M7 17L17 7M17 7H7M17 7v10"
    />
  </svg>
);

interface ButtonConfig {
  label: React.ReactNode;
  icon: React.ReactNode;
}

const microsoftButtonConfig: ButtonConfig = {
  label: (
    <T context="button label to sign in with Microsoft SSO on the login page">
      Sign in with Microsoft
    </T>
  ),
  icon: <MicrosoftLogo />,
};

const googleButtonConfig: ButtonConfig = {
  label: (
    <T context="button label to sign in with Google SSO on the login page">
      Sign in with Google
    </T>
  ),
  icon: <GoogleLogo />,
};

const CONNECTION_TYPE_BUTTON_CONFIG: Partial<
  Record<ConnectionType, ButtonConfig>
> = {
  MicrosoftOAuth: microsoftButtonConfig,
  AzureSAML: microsoftButtonConfig,
  GoogleOAuth: googleButtonConfig,
  GoogleSAML: googleButtonConfig,
};

export function isSupportedConnectionType(type: string): boolean {
  return type in CONNECTION_TYPE_BUTTON_CONFIG;
}

export function SsoLoginButton({
  connectionType,
  onClick,
  disabled,
  isLoading,
}: SsoLoginButtonProps) {
  const config =
    connectionType != null
      ? (CONNECTION_TYPE_BUTTON_CONFIG[connectionType as ConnectionType] ??
        null)
      : null;

  return (
    <Button
      type="button"
      variant="outline"
      disabled={disabled}
      onClick={onClick}
      className="w-full gap-2"
    >
      {isLoading ? (
        <Spinner />
      ) : config != null ? (
        <>
          {config.icon}
          {config.label}
        </>
      ) : (
        <>
          <T context="button label to start SSO on the login page">
            Log in with SSO
          </T>
          <GenericSsoIcon />
        </>
      )}
    </Button>
  );
}
